import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Form, Button, Alert, ButtonGroup, Dropdown } from 'react-bootstrap';
import { Plus, X, Check } from 'react-bootstrap-icons';
import moment from 'moment-timezone';
import { useTheme } from '../../contexts/ThemeContext';
import useIsMobile from '../../hooks/useIsMobile';
import useAPI from '../../hooks/useApi';
import { useRealtimeAccountMetrics } from '../../hooks/useRealtimeAccountMetrics';
import { AuthContext } from '../../contexts/AuthContext';
import { useEnforcementAlerts } from '../../contexts/EnforcementAlertsContext';
import { useTradingRulesConfig } from '../../contexts/TradingRulesConfigContext';
import useTradingRulesMetrics from '../../hooks/useTradingRulesMetrics';
import analyticsService from '../../services/analyticsService';
import CompactModeSelector from './CompactModeSelector';
import ExpandableDescription, { useExpandableDescription } from './ExpandableDescription';
import useBrokerCapabilities from '../../hooks/useBrokerCapabilities';
import {
  supportsBracketDependentRuleEnforcement,
  BRACKET_DEPENDENT_RULE_ENFORCE_UNSUPPORTED_TOOLTIP,
} from '../../utils/brokerConnection';
import { formatLockoutExpiresAt } from '../../utils/lockoutTimeFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ET_TIMEZONE = 'America/New_York';

// 'end_of_day' is kept only for backward compatibility with previously-saved configs —
// both resolve to the same 11:59 p.m. ET timestamp (see getAbsoluteExpirationDateIso)
// and the backend has always treated 'eod' as end-of-day, never 4 p.m. session close.
const EXPIRATION_TYPE_VALUES = ['eod', 'end_of_day', '1_week', '1_month', 'custom'];

const EXPIRATION_OPTIONS = [
  { value: 'eod', label: 'End of day (11:59 p.m. ET)' },
  { value: '1_week', label: '1 week' },
  { value: '1_month', label: '1 month' },
  { value: 'custom', label: 'Custom' },
];

function getRuleCardSurfaceStyle(ruleMode, colors, isLight) {
  const primary = colors.primary || '#6366F1';
  const success = colors.success || '#07CBA3';
  const warning = colors.warning || '#ED8936';
  const greySurface = colors.bgInput || colors.bgSecondary || '#14161c';
  // Bright, saturated blue-purple for track (monitoring) vs teal enforce / grey off.
  const trackBlue = '#6366F1';

  // On light theme, low-alpha tints over a near-white card read as faint,
  // uneven blotches rather than a clean gradient — use richer, more saturated
  // pastel alphas (and a stronger border) so the state reads clearly.
  const trackAlphaLeft = isLight ? '32' : '24';
  const trackAlphaRight = isLight ? '08' : '05';
  const enforceAlpha = isLight ? '30' : '24';
  const enforcePrimaryAlpha = isLight ? '20' : '1a';
  const alertAlpha = isLight ? '22' : '14';

  switch (ruleMode) {
    case 'off':
      return {
        background: greySurface,
        border: `1px solid ${colors.border}`,
      };
    case 'track':
      return {
        background: `linear-gradient(90deg, ${trackBlue}${trackAlphaLeft} 0%, ${trackBlue}${trackAlphaRight} 100%)`,
        border: `1px solid ${trackBlue}${isLight ? '55' : '40'}`,
        boxShadow: `inset 0 1px 0 ${trackBlue}18`,
      };
    case 'alert':
      return {
        background: `${warning}${alertAlpha}`,
        border: `1px solid ${warning}${isLight ? '55' : '40'}`,
      };
    case 'enforce':
      return {
        background: `linear-gradient(135deg, ${success}${enforceAlpha} 0%, ${primary}${enforcePrimaryAlpha} 100%)`,
        border: `1px solid ${success}${isLight ? '70' : '55'}`,
        boxShadow: `inset 0 1px 0 ${success}28`,
      };
    default:
      return {
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
      };
  }
}

const RULE_STATUS_PILL_BASE = {
  fontSize: '0.65rem',
  padding: '2px 8px',
  fontWeight: 500,
  borderRadius: '12px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

function RuleStatusPill({ children, color, backgroundColor, borderColor }) {
  return (
    <span
      style={{
        ...RULE_STATUS_PILL_BASE,
        color,
        backgroundColor,
        border: `1px solid ${borderColor}`,
      }}
    >
      {children}
    </span>
  );
}

const RuleCard = ({ title, titleSuffix, titleSuffixTooltip, description, mode, onModeChange, showExpiration = false, expirationValue, onExpirationChange, customDateValue, onCustomDateChange, onConfirmEnforce, children, error, ruleKey, restrictsTrading = false, restrictionText = "Restricts trading when limit is hit", alertOnly = false, enforcedExpirationTime, cooldownExpiresTime, expirationDropdownKey, expirationDropdownOpen, onExpirationDropdownToggle, limitExceeded = false, outsideHours = false, disabledModes = [], disabledModeTooltips = {} }) => {
  const { colors, isLight } = useTheme();
  const isMobile = useIsMobile(768, 1024);
  const isLocked = !!enforcedExpirationTime;
  const { toggle: descriptionToggle, body: descriptionBody } = useExpandableDescription(description);
  const cardSurface = getRuleCardSurfaceStyle(mode, colors, isLight);
  const danger = colors.danger || '#DC2626';
  const cooldownBlue = '#38BDF8';
  const showLimitExceededPill = limitExceeded && mode === 'enforce';
  const statusPills = [];

  if (showLimitExceededPill) {
    statusPills.push(
      <RuleStatusPill
        key="limit-exceeded"
        color={danger}
        backgroundColor={`${danger}20`}
        borderColor={danger}
      >
        Limit exceeded
      </RuleStatusPill>
    );
  }
  if (cooldownExpiresTime) {
    statusPills.push(
      <RuleStatusPill
        key="cooldown-active"
        color={cooldownBlue}
        backgroundColor="rgba(56, 189, 248, 0.15)"
        borderColor={cooldownBlue}
      >
        Cooldown active
      </RuleStatusPill>
    );
  }
  if (outsideHours) {
    statusPills.push(
      <RuleStatusPill
        key="outside-hours"
        color="#3B82F6"
        backgroundColor="rgba(59, 130, 246, 0.15)"
        borderColor="#3B82F6"
      >
        Outside hours
      </RuleStatusPill>
    );
  }

  return (
    <div style={{
      ...cardSurface,
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '12px',
      display: 'flex',
      flexDirection: 'column',
      minHeight: '80px',
      transition: 'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
    }}>
      {/* Row 1: title, expand, mode selector — stack on mobile so long titles never overlap controls */}
      <div
        data-testid="rule-card-header"
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '10px' : '8px',
          marginBottom: descriptionBody ? '4px' : '8px',
          width: '100%',
          minWidth: 0,
        }}
      >
        <div
          data-testid="rule-card-title-row"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            flex: isMobile ? '0 0 auto' : 1,
            minWidth: 0,
            flexWrap: 'wrap',
          }}
        >
          <div style={{
            fontWeight: 600,
            fontSize: '0.95rem',
            color: colors.text,
            lineHeight: 1.3,
            minWidth: 0,
            flex: '1 1 auto',
            overflowWrap: 'anywhere',
          }}>
            {title}
            {titleSuffix && (
              <span title={titleSuffixTooltip} style={{ fontWeight: 500, color: colors.textMuted, marginLeft: 4 }}>
                {titleSuffix}
              </span>
            )}
          </div>
          {descriptionToggle}
          {cooldownExpiresTime && (
            <div style={{
              fontSize: '0.75rem',
              color: cooldownBlue,
              fontWeight: 500,
              whiteSpace: isMobile ? 'normal' : 'nowrap',
              flexShrink: 0,
              width: isMobile ? '100%' : undefined,
            }}>
              Cooldown ends: {cooldownExpiresTime}
            </div>
          )}
          {enforcedExpirationTime && (
            <div style={{
              fontSize: '0.75rem',
              color: colors.textMuted,
              whiteSpace: isMobile ? 'normal' : 'nowrap',
              flexShrink: 0,
              width: isMobile ? '100%' : undefined,
            }}>
              Unlocks: {enforcedExpirationTime}
            </div>
          )}
        </div>
        <div
          data-testid="rule-card-mode-selector"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'flex-start' : 'flex-end',
            marginLeft: isMobile ? 0 : 'auto',
            flexShrink: 0,
            width: isMobile ? '100%' : 'auto',
            maxWidth: '100%',
          }}
        >
          <CompactModeSelector
            value={mode}
            onChange={onModeChange}
            alertOnly={alertOnly}
            locked={isLocked}
            disabledModes={disabledModes}
            disabledModeTooltips={disabledModeTooltips}
            fullWidth={isMobile}
          />
        </div>
      </div>
      {descriptionBody}
        
        {/* Inputs + expiration controls */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'flex-start',
        gap: '16px',
        flexWrap: 'wrap'
      }}>
        {children ? (
        <div style={{
          flex: isMobile ? '0 0 auto' : '0 1 50%',
          minWidth: isMobile ? undefined : '120px',
          maxWidth: isMobile ? '100%' : '50%',
          width: isMobile ? '100%' : undefined
        }}>
          <div>{children}</div>
        </div>
        ) : null}

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isMobile ? 'flex-start' : 'flex-end',
          gap: '8px',
          flex: children ? (isMobile ? '0 0 auto' : '1 1 0') : '1 1 auto',
          minWidth: 0,
          width: isMobile ? '100%' : undefined,
          marginLeft: children ? undefined : 'auto',
        }}>
          {showExpiration && mode === 'enforce' && !enforcedExpirationTime && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
              justifyContent: 'flex-end'
            }}>
              <Dropdown
                show={expirationDropdownOpen}
                onToggle={(isOpen) => onExpirationDropdownToggle?.(isOpen ? expirationDropdownKey : null)}
                align="end"
                style={{ alignSelf: 'flex-end' }}
              >
                <Dropdown.Toggle
                  as={Button}
                  variant="outline-secondary"
                  size="sm"
                  style={{
                    fontSize: '0.7rem',
                    padding: '2px 8px',
                    minHeight: '26px',
                    minWidth: '160px',
                    width: 'auto',
                    maxWidth: '100%',
                    backgroundColor: colors.bgCard,
                    borderColor: colors.border,
                    color: colors.text,
                    fontWeight: 400,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    textAlign: 'right',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {EXPIRATION_OPTIONS.find(o => o.value === expirationValue)?.label ?? expirationValue}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    backgroundColor: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    padding: '4px 0',
                    marginTop: 4,
                    minWidth: '200px',
                    fontSize: '0.7rem',
                    zIndex: 1060
                  }}
                >
                  {EXPIRATION_OPTIONS.map(opt => (
                    <Dropdown.Item
                      key={opt.value}
                      onClick={() => { onExpirationChange(opt.value); onExpirationDropdownToggle?.(null); }}
                      style={{
                        color: opt.value === expirationValue ? colors.primary : colors.text,
                        padding: '6px 10px',
                        backgroundColor: opt.value === expirationValue ? `${colors.primary}18` : 'transparent',
                        fontWeight: opt.value === expirationValue ? 600 : 400
                      }}
                    >
                      {opt.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <button
                onClick={onConfirmEnforce}
                style={{
                  background: colors.primary,
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0
                }}
                type="button"
                title="Apply enforcement"
              >
                <Check size={12} style={{ color: '#fff', fontWeight: 'bold' }} />
              </button>
              {expirationValue === 'custom' && (
                <Form.Control
                  type="datetime-local"
                  size="sm"
                  value={customDateValue}
                  onChange={(e) => onCustomDateChange(e.target.value)}
                  style={{
                    fontSize: '0.7rem',
                    padding: '2px 6px',
                    height: '26px',
                    backgroundColor: colors.bgCard,
                    borderColor: colors.border,
                    color: colors.text,
                    width: 'auto',
                    minWidth: '160px'
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          fontSize: '0.7rem',
          color: colors.danger,
          marginTop: '4px',
          fontStyle: 'italic'
        }}>
          {error}
        </div>
      )}

      {statusPills.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '6px',
          marginTop: 'auto',
          paddingTop: '8px',
        }}>
          {statusPills}
        </div>
      )}
    </div>
  );
};

const SectionHeader = ({ title, subtitle, variant = 'default' }) => {
  const { colors } = useTheme();
  const expandableSubtitle = typeof subtitle === 'string' ? subtitle : null;
  const { toggle: subtitleToggle, body: subtitleBody } = useExpandableDescription(expandableSubtitle, {
    style: { fontSize: '0.8rem', margin: '8px 0 0' },
  });
  if (variant === 'tradingRulesQuick' && subtitle) {
    const quickCopyBase = {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
      color: colors.textMuted,
    };
    return (
      <div style={{ marginBottom: '16px', marginTop: 0, ...quickCopyBase }}>
        {typeof subtitle === 'string' ? (
          <p style={{ margin: 0 }}>{subtitle}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {subtitle}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '20px', marginTop: title === 'Trading Rules' ? '0' : '32px' }}>
      <h3 style={{
        fontSize: '0.85rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: colors.textMuted,
        margin: '0 0 4px 0',
        paddingBottom: '8px',
        borderBottom: `2px solid ${colors.border}`,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span>{title}</span>
        {subtitleToggle}
      </h3>
      {subtitleBody}
    </div>
  );
};

const ManualLockoutCard = ({
  mode, onModeChange, saving, isActive, lockoutExpirationTime,
  expiration, onExpirationChange, customDate, onCustomDateChange,
  onConfirmEnforce, expirationDropdownOpen, onExpirationDropdownToggle,
}) => {
  const { colors, isLight } = useTheme();
  const isMobile = useIsMobile(768, 1024);
  const danger = colors.danger || '#DC2626';
  const isLockEngaged = mode === 'enforce' || isActive;
  // On light theme, a low-alpha red tint over a near-white card barely shows,
  // so the hardcoded white title/body text (tuned for dark theme) becomes
  // illegible. Use a richer tint + theme text color in light mode.
  const lockoutTextColor = isLight ? colors.text : '#fff';

  return (
    <div style={{
      background: isLight
        ? `linear-gradient(135deg, ${danger}2e 0%, ${danger}18 55%, ${danger}0c 100%)`
        : `linear-gradient(135deg, ${danger}38 0%, ${danger}1a 55%, ${danger}0d 100%)`,
      border: `2px solid ${isLockEngaged ? danger : `${danger}80`}`,
      boxShadow: isLockEngaged ? `0 0 12px ${danger}33` : `inset 0 1px 0 ${danger}25`,
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '24px'
    }}>
      <div style={{ marginBottom: '10px' }}>
        <div style={{ fontWeight: 600, fontSize: '0.95rem', color: lockoutTextColor }}>
          Manual Trading Lockout
        </div>
        <p style={{
          fontSize: '0.75rem',
          color: lockoutTextColor,
          lineHeight: 1.35,
          margin: '6px 0 0',
          opacity: 0.92,
        }}>
          Pause all new trades. You can only close existing positions.
        </p>
      </div>
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'flex-end',
        gap: '16px',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isMobile ? 'flex-start' : 'flex-end',
          gap: '8px',
          flex: '1 1 auto',
          minWidth: 0,
          width: isMobile ? '100%' : undefined
        }}>
          <ButtonGroup size="sm" style={{ minHeight: 'unset' }}>
            <Button
              variant={mode === 'off' ? 'primary' : 'outline-secondary'}
              onClick={() => onModeChange(false)}
              disabled={saving || isActive}
              style={{
                padding: '2px 6px', fontSize: '0.7rem',
                fontWeight: mode === 'off' ? 600 : 400,
                borderColor: mode === 'off' ? colors.primary : colors.border,
                backgroundColor: mode === 'off' ? colors.primary : 'transparent',
                color: mode === 'off' ? '#fff' : colors.text,
                minHeight: '26px', lineHeight: 1.2
              }}
            >Off</Button>
            <Button
              variant={mode === 'enforce' ? 'danger' : 'outline-secondary'}
              onClick={() => onModeChange(true)}
              disabled={saving}
              style={{
                padding: '2px 6px', fontSize: '0.7rem',
                fontWeight: mode === 'enforce' ? 600 : 400,
                borderColor: mode === 'enforce' ? danger : `${danger}66`,
                backgroundColor: mode === 'enforce' ? danger : 'transparent',
                color: mode === 'enforce' ? '#fff' : danger,
                minHeight: '26px', lineHeight: 1.2
              }}
            >{mode === 'enforce' || isActive ? 'Enabled' : 'Enable'}</Button>
          </ButtonGroup>
          {isActive && (
            <div style={{
              fontSize: '0.75rem',
              color: lockoutTextColor,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              textAlign: isMobile ? 'left' : 'right'
            }}>
              Unlocks: {lockoutExpirationTime}
            </div>
          )}
          {mode === 'enforce' && !isActive && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Dropdown
                show={expirationDropdownOpen}
                onToggle={onExpirationDropdownToggle}
                align="end"
                style={{ alignSelf: 'flex-end' }}
              >
                <Dropdown.Toggle
                  as={Button}
                  variant="outline-secondary"
                  size="sm"
                  style={{
                    fontSize: '0.7rem', padding: '2px 8px', minHeight: '26px',
                    minWidth: '160px', width: 'auto', maxWidth: '100%',
                    backgroundColor: colors.bgCard, borderColor: colors.border, color: colors.text,
                    fontWeight: 400, display: 'flex', alignItems: 'center',
                    justifyContent: 'flex-end', textAlign: 'right', whiteSpace: 'nowrap'
                  }}
                >
                  {EXPIRATION_OPTIONS.find(o => o.value === (expiration || 'eod'))?.label ?? (expiration || 'eod')}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    backgroundColor: colors.bgCard, border: `1px solid ${colors.border}`,
                    borderRadius: '6px', padding: '4px 0', marginTop: 4,
                    minWidth: '200px', fontSize: '0.7rem', zIndex: 1060
                  }}
                >
                  {EXPIRATION_OPTIONS.map(opt => (
                    <Dropdown.Item
                      key={opt.value}
                      onClick={() => onExpirationChange(opt.value)}
                      style={{
                        color: opt.value === (expiration || 'eod') ? colors.primary : colors.text,
                        padding: '6px 10px',
                        backgroundColor: opt.value === (expiration || 'eod') ? `${colors.primary}18` : 'transparent',
                        fontWeight: opt.value === (expiration || 'eod') ? 600 : 400
                      }}
                    >
                      {opt.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <button
                onClick={onConfirmEnforce}
                disabled={saving}
                style={{
                  background: danger, border: 'none', borderRadius: '50%',
                  width: '24px', height: '24px', minWidth: '24px', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: saving ? 'wait' : 'pointer', padding: 0
                }}
                type="button"
                title="Apply lockout"
              >
                <Check size={12} style={{ color: '#fff', fontWeight: 'bold' }} />
              </button>
              {expiration === 'custom' && (
                <Form.Control
                  type="datetime-local"
                  size="sm"
                  value={customDate}
                  onChange={(e) => onCustomDateChange(e.target.value)}
                  style={{
                    fontSize: '0.7rem', padding: '2px 6px', height: '26px',
                    backgroundColor: colors.bgCard, borderColor: colors.border,
                    color: colors.text, width: 'auto', minWidth: '160px'
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const TradingWindowsList = ({
  windows,
  onWindowChange,
  onAdd,
  onRemove,
  isLocked,
  addButtonOnNextRow = false,
}) => {
  const { colors } = useTheme();

  const addBtn = (
    <button
      onClick={onAdd}
      disabled={isLocked}
      type="button"
      style={{
        width: '24px',
        height: '24px',
        minWidth: '24px',
        flexShrink: 0,
        borderRadius: '50%',
        backgroundColor: colors.primary,
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isLocked ? 'not-allowed' : 'pointer',
        padding: 0,
        opacity: isLocked ? 0.5 : 1,
      }}
    >
      <Plus size={14} style={{ color: '#fff' }} />
    </button>
  );

  return (
    <div style={{ width: '100%' }}>
      {windows.map((window, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: index === windows.length - 1 && !addButtonOnNextRow ? '0' : '8px',
            flexWrap: 'wrap',
          }}
        >
          <Form.Control
            type="time"
            value={window.startTime}
            onChange={(e) => onWindowChange(index, 'startTime', e.target.value)}
            disabled={isLocked}
            style={{ width: '130px', backgroundColor: colors.bgCard, borderColor: colors.border, color: colors.text, fontSize: '0.85rem', opacity: isLocked ? 0.6 : 1 }}
          />
          <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>—</span>
          <Form.Control
            type="time"
            value={window.endTime}
            onChange={(e) => onWindowChange(index, 'endTime', e.target.value)}
            disabled={isLocked}
            style={{ width: '130px', backgroundColor: colors.bgCard, borderColor: colors.border, color: colors.text, fontSize: '0.85rem', opacity: isLocked ? 0.6 : 1 }}
          />
          {windows.length > 1 && (
            <button
              onClick={() => onRemove(index)}
              disabled={isLocked}
              type="button"
              style={{
                width: '24px', height: '24px', minWidth: '24px', flexShrink: 0,
                borderRadius: '50%', backgroundColor: colors.danger, border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: isLocked ? 'not-allowed' : 'pointer', padding: 0, opacity: isLocked ? 0.5 : 1
              }}
            >
              <X size={12} style={{ color: '#fff' }} />
            </button>
          )}
          {index === windows.length - 1 && !addButtonOnNextRow ? addBtn : null}
        </div>
      ))}
      {addButtonOnNextRow && !isLocked ? (
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 4 }}>{addBtn}</div>
      ) : null}
    </div>
  );
};

const BehavioralTrackingSection = ({ trackStopMods, trackJournal, onTrackStopModsChange, onTrackJournalChange }) => {
  const { colors } = useTheme();

  return (
    <div style={{
      padding: '16px',
      background: colors.bgCard,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      marginBottom: '12px'
    }}>
      <Form.Check
        type="checkbox"
        id="track-stop-modifications"
        checked={trackStopMods}
        onChange={(e) => onTrackStopModsChange(e.target.checked)}
        label={
          <ExpandableDescription
            title="Stop/Take Profit Modifications"
            titleStyle={{ fontWeight: 500, fontSize: '0.9rem' }}
            text="Track when brackets are modified after entry"
          />
        }
        style={{ marginBottom: '16px' }}
      />
      <Form.Check
        type="checkbox"
        id="track-journal"
        checked={trackJournal}
        onChange={(e) => onTrackJournalChange(e.target.checked)}
        label={
          <ExpandableDescription
            title="Journal Tracking"
            titleStyle={{ fontWeight: 500, fontSize: '0.9rem' }}
            text="Track days with trades vs journal entries"
          />
        }
      />
    </div>
  );
};

/**
 * TradingRulesForm - Compact row-based capital protection interface
 */
const TradingRulesForm = forwardRef(
  (
    {
      accountId,
      settings,
      saveSettings,
      standalone = false,
      onSave,
      layoutVariant = 'default',
      quickSetupOnly = false,
      trackTier = false,
    },
    ref
  ) => {
  const { colors } = useTheme();
  const isMobile = useIsMobile(768, 1024);
  const { request } = useAPI();
  const { user } = useContext(AuthContext);
  const { data: brokerCapabilities } = useBrokerCapabilities(user);
  const bracketDependentEnforceSupported = supportsBracketDependentRuleEnforcement(brokerCapabilities);
  const stopLossEnforceSupported = bracketDependentEnforceSupported;
  const maxRiskEnforceSupported = bracketDependentEnforceSupported;
  const { todayChange } = useRealtimeAccountMetrics();
  const metricsAccount = accountId ? { AccountID: accountId } : null;
  const { metrics: todayMetrics } = useTradingRulesMetrics(metricsAccount, 'today');
  const todayTradesTakenRaw = todayMetrics?.metrics?.trades_taken ?? todayMetrics?.trades_taken;
  const todayTradesTaken = Number.isFinite(Number(todayTradesTakenRaw))
    ? Number(todayTradesTakenRaw)
    : null;
  const { fetchPendingAlerts, pendingAlerts } = useEnforcementAlerts();
  const { applySettings } = useTradingRulesConfig();
  
  const [formData, setFormData] = useState({
    max_trades_per_day: '',
    max_loss_per_day: '',
    profit_target_per_day: '100',
    consecutive_losses: '2',
    consecutive_loss_cooldown_minutes: '30',
    max_loss_per_position: '',
    max_risk_per_trade: '',
    trading_window_timezone: 'America/New_York'
  });
  
  const [tradingWindows, setTradingWindows] = useState([
    { startTime: '09:45', endTime: '11:00' }
  ]);
  
  const [modes, setModes] = useState({
    max_loss_per_day: 'track',
    profit_target_per_day: 'track',
    consecutive_loss_cooldown: 'track',
    max_trades_per_day: 'track',
    trading_window: 'track',
    max_loss_per_position: 'track',
    max_risk_per_trade: 'track',
    require_stop_loss: 'off',
    manual_lockout: 'off',
    stop_take_profit_modifications: 'track',
    journal_tracking: 'track'
  });
  
  const [expirations, setExpirations] = useState({
    max_loss_per_day: 'eod',
    profit_target_per_day: 'eod',
    consecutive_loss_cooldown: 'eod',
    max_trades_per_day: 'eod',
    manual_lockout: 'eod',
    trading_window: 'eod',
    require_stop_loss: 'eod',
    max_risk_per_trade: 'eod'
  });
  
  const [customDates, setCustomDates] = useState({
    max_loss_per_day: '',
    profit_target_per_day: '',
    consecutive_loss_cooldown: '',
    max_trades_per_day: '',
    manual_lockout: '',
    trading_window: '',
    require_stop_loss: '',
    max_risk_per_trade: ''
  });
  
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [saveError, setSaveError] = useState(null);
  const [, setEnforcedExpirations] = useState({});
  const [manualLockoutSaving, setManualLockoutSaving] = useState(false);
  const [openExpirationKey, setOpenExpirationKey] = useState(null);
  const [manualLockoutExpirationOpen, setManualLockoutExpirationOpen] = useState(false);
  const [currentMinutes, setCurrentMinutes] = useState(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  });
  const isSavingRef = useRef(false);
  const lastSavedSettingsRef = useRef(null);
  const focusedFieldRef = useRef(null);

  // Keep currentMinutes in sync so the outside-hours pill updates every minute
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setCurrentMinutes(now.getHours() * 60 + now.getMinutes());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const getAbsoluteExpirationDateIso = (expirationType, customDate) => {
    const tz = 'America/New_York';
    const now = moment().tz(tz);
    if (customDate) {
      const explicit = moment(customDate);
      if (explicit.isValid()) return explicit.toISOString();
    }
    if (expirationType === 'eod' || expirationType === 'end_of_day') {
      // 'eod' means end of day (11:59 p.m. ET), matching backend semantics everywhere
      // this is enforced — not 4 p.m. market close. 'end_of_day' is the same value,
      // kept only so previously-saved configs still resolve correctly.
      let dayEnd = now.clone().hour(23).minute(59).second(0).millisecond(0);
      if (dayEnd.isBefore(now)) dayEnd = dayEnd.add(1, 'day');
      return dayEnd.toISOString();
    }
    if (expirationType === '1_week') {
      return now.clone().add(1, 'week').toISOString();
    }
    if (expirationType === '1_month') {
      return now.clone().add(1, 'month').toISOString();
    }
    return null;
  };

  const formatExpirationTime = (expirationType, customDate) => {
    const absoluteIso = getAbsoluteExpirationDateIso(expirationType, customDate);
    if (!absoluteIso) return null;
    return moment(absoluteIso).tz(ET_TIMEZONE).format('ddd, MMM D, h:mm A');
  };

  const handleModeChangeWithSave = async (rule, mode) => {
    // Prevent switching away from enforce if rule is locked
    if (modes[rule] === 'enforce' && isRuleLocked(rule)) {
      return;
    }
    // Brokers without bracket/OCO cannot enforce require-stop-loss or max-risk (tracking still allowed)
    if (
      (rule === 'require_stop_loss' || rule === 'max_risk_per_trade') &&
      mode === 'enforce' &&
      !bracketDependentEnforceSupported
    ) {
      return;
    }
    handleModeChange(rule, mode);

    // Ensure threshold defaults exist before an eager track/alert save (existing configs may lack new rules)
    const formDataOverrides = {};
    if (mode === 'track' || mode === 'alert') {
      if (rule === 'profit_target_per_day' && !String(formData.profit_target_per_day || '').trim()) {
        formDataOverrides.profit_target_per_day = '100';
        setFormData((prev) => ({ ...prev, profit_target_per_day: '100' }));
      }
      if (rule === 'consecutive_loss_cooldown') {
        if (!String(formData.consecutive_losses || '').trim()) {
          formDataOverrides.consecutive_losses = '2';
        }
        if (!String(formData.consecutive_loss_cooldown_minutes || '').trim()) {
          formDataOverrides.consecutive_loss_cooldown_minutes = '30';
        }
        if (Object.keys(formDataOverrides).length > 0) {
          setFormData((prev) => ({ ...prev, ...formDataOverrides }));
        }
      }
    }

    // Save immediately when switching to Track, Alert, or Off (eager update via API response). Enforce only saves when they click check.
    if (mode === 'track' || mode === 'alert' || mode === 'off') {
      await handleSave(false, { [rule]: mode }, { formDataOverrides });
    }
  };

  useEffect(() => {
    if (isSavingRef.current || !settings) return;

    // Only reload when enforcement config actually changed (not on every settings object reference change)
    const currentEnforcementConfig = settings.enforcement_config || {};
    const lastEnforcementConfig = lastSavedSettingsRef.current?.enforcement_config || {};
    const rulesToCheck = ['max_loss_per_day', 'profit_target_per_day', 'consecutive_loss_cooldown', 'max_trades_per_day', 'trading_window', 'max_loss_per_position', 'max_risk_per_trade', 'require_stop_loss', 'manual_lockout', 'stop_take_profit_modifications', 'journal_tracking'];

    const hasChanged = !lastSavedSettingsRef.current || rulesToCheck.some(rule => {
      const curr = currentEnforcementConfig[rule];
      const last = lastEnforcementConfig[rule];
      return curr?.enabled !== last?.enabled || curr?.enforce !== last?.enforce || curr?.alert !== last?.alert;
    });

    if (!hasChanged) return;

    lastSavedSettingsRef.current = JSON.parse(JSON.stringify(settings));

    const thresholds = settings.rule_thresholds || {};
    const enforcementConfig = settings.enforcement_config || {};

    // Don't overwrite the field the user is currently focused on
    setFormData(prev => {
      const focused = focusedFieldRef.current;
      return {
        max_trades_per_day:     focused === 'max_trades_per_day'     ? prev.max_trades_per_day     : (thresholds.max_trades_per_day?.toString() || ''),
        max_loss_per_day:       focused === 'max_loss_per_day'       ? prev.max_loss_per_day       : (thresholds.max_loss_per_day ? Math.abs(thresholds.max_loss_per_day).toString() : ''),
        profit_target_per_day: focused === 'profit_target_per_day' ? prev.profit_target_per_day : (thresholds.profit_target_per_day ? Math.abs(thresholds.profit_target_per_day).toString() : '100'),
        consecutive_losses:    focused === 'consecutive_losses'    ? prev.consecutive_losses    : (thresholds.consecutive_losses?.toString() || '2'),
        consecutive_loss_cooldown_minutes: focused === 'consecutive_loss_cooldown_minutes' ? prev.consecutive_loss_cooldown_minutes : (thresholds.consecutive_loss_cooldown_minutes?.toString() || '30'),
        max_loss_per_position:  focused === 'max_loss_per_position'  ? prev.max_loss_per_position  : (thresholds.max_loss_per_position ? Math.abs(thresholds.max_loss_per_position).toString() : ''),
        max_risk_per_trade:     focused === 'max_risk_per_trade'     ? prev.max_risk_per_trade     : (thresholds.max_risk_per_trade ? thresholds.max_risk_per_trade.toString() : ''),
        trading_window_timezone: ET_TIMEZONE,
      };
    });
    
    if (thresholds.trading_windows && Array.isArray(thresholds.trading_windows)) {
      setTradingWindows(thresholds.trading_windows.map(w => ({
        startTime: w.startTime?.substring(0, 5) || w.start_time?.substring(0, 5) || '09:45',
        endTime: w.endTime?.substring(0, 5) || w.end_time?.substring(0, 5) || '11:00'
      })));
    }
    
    const convertToMode = (rule) => {
      const enabled = enforcementConfig[rule]?.enabled;
      const enforce = enforcementConfig[rule]?.enforce;
      const alert = enforcementConfig[rule]?.alert;
      if (rule === 'max_loss_per_position' && enabled && !enforce && alert) return 'alert';
      if (trackTier && enforce) return 'alert';
      if (trackTier && enabled && alert) return 'alert';
      // Cannot enforce bracket-dependent rules without broker OCO support — show as Track
      if (
        (rule === 'require_stop_loss' || rule === 'max_risk_per_trade') &&
        enforce &&
        !bracketDependentEnforceSupported
      ) {
        return 'track';
      }
      if (enforce) return 'enforce';
      if (enabled) return 'track';
      return 'off';
    };
    
    setModes({
      max_loss_per_day: convertToMode('max_loss_per_day'),
      profit_target_per_day: convertToMode('profit_target_per_day'),
      consecutive_loss_cooldown: convertToMode('consecutive_loss_cooldown'),
      max_trades_per_day: convertToMode('max_trades_per_day'),
      trading_window: convertToMode('trading_window'),
      max_loss_per_position: convertToMode('max_loss_per_position'),
      max_risk_per_trade: convertToMode('max_risk_per_trade'),
      require_stop_loss: convertToMode('require_stop_loss'),
      manual_lockout: enforcementConfig.manual_lockout?.enforce ? 'enforce' : 'off',
      stop_take_profit_modifications: enforcementConfig.stop_take_profit_modifications?.enabled ? 'track' : 'off',
      journal_tracking: enforcementConfig.journal_tracking?.enabled ? 'track' : 'off'
    });
    
    // Load expirations and custom dates from enforcement_config
    const loadedExpirations = {};
    const loadedCustomDates = {};
    
    ['max_loss_per_day', 'profit_target_per_day', 'consecutive_loss_cooldown', 'max_trades_per_day', 'trading_window', 'require_stop_loss', 'max_risk_per_trade'].forEach(rule => {
      if (enforcementConfig[rule]?.expiration) {
        const exp = enforcementConfig[rule].expiration;
        // Normalize legacy 'end_of_day' to 'eod' so the dropdown label resolves
        // (EXPIRATION_OPTIONS only has an 'eod' entry; both values mean the same thing).
        const t = exp.type === 'end_of_day' ? 'eod' : (exp.type || 'eod');
        loadedExpirations[rule] = EXPIRATION_TYPE_VALUES.includes(t) ? t : 'eod';
        if (exp.type === 'custom' && exp.date) {
          loadedCustomDates[rule] = exp.date;
        }
      }
    });
    
    if (Object.keys(loadedExpirations).length > 0) {
      setExpirations(prev => ({ ...prev, ...loadedExpirations }));
    }
    if (Object.keys(loadedCustomDates).length > 0) {
      setCustomDates(prev => ({ ...prev, ...loadedCustomDates }));
    }
    
    // Load enforced expiration displays - show pill if enforcement is active regardless of mode selector
    const loadedEnforcedExpirations = {};
    ['max_loss_per_day', 'profit_target_per_day', 'consecutive_loss_cooldown', 'max_trades_per_day', 'trading_window', 'require_stop_loss', 'max_risk_per_trade'].forEach(rule => {
      if (enforcementConfig[rule]?.enforce && enforcementConfig[rule]?.expiration) {
        const exp = enforcementConfig[rule].expiration;
        const formatted = formatExpirationTime(exp.type, exp.date);
        if (formatted) {
          loadedEnforcedExpirations[rule] = formatted;
        }
      }
    });
    if (Object.keys(loadedEnforcedExpirations).length > 0) {
      setEnforcedExpirations(loadedEnforcedExpirations);
    }
  }, [settings, trackTier, bracketDependentEnforceSupported]);

  // If broker cannot attach stop-losses, demote stale Enforce selection to Track in the UI
  useEffect(() => {
    if (bracketDependentEnforceSupported) return;
    setModes((prev) => {
      let next = prev;
      if (prev.require_stop_loss === 'enforce') {
        next = { ...next, require_stop_loss: 'track' };
      }
      if (prev.max_risk_per_trade === 'enforce') {
        next = { ...next, max_risk_per_trade: 'track' };
      }
      return next;
    });
  }, [bracketDependentEnforceSupported]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleInputBlur = async (field, rawValue) => {
    focusedFieldRef.current = null;

    const currentValue = rawValue != null ? String(rawValue) : formData[field];
    const thresholdValue = settings?.rule_thresholds?.[field];
    const expectedValue = thresholdValue != null && thresholdValue !== ''
      ? (field === 'max_risk_per_trade' || field === 'max_trades_per_day'
        ? thresholdValue.toString()
        : Math.abs(thresholdValue).toString())
      : '';

    // Auto-save on blur when the value changed
    if (currentValue !== expectedValue) {
      if (rawValue != null && formData[field] !== currentValue) {
        setFormData((prev) => ({ ...prev, [field]: currentValue }));
      }
      await handleSave(true, {}, { formDataOverrides: { [field]: currentValue } });
    }
  };

  const handleModeChange = (rule, mode) => {
    setModes(prev => ({ ...prev, [rule]: mode }));
  };
  
  const handleExpirationChange = (rule, expiration) => {
    setExpirations(prev => ({ ...prev, [rule]: expiration }));
    
    // When switching to custom, default to end of trading day (4:00 PM) if no datetime set
    if (expiration === 'custom' && !customDates[rule]) {
      const today = new Date();
      today.setHours(16, 0, 0, 0); // 4:00 PM
      const formattedDateTime = today.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:mm
      setCustomDates(prev => ({ ...prev, [rule]: formattedDateTime }));
    }
  };
  
  const handleCustomDateChange = (rule, date) => {
    setCustomDates(prev => ({ ...prev, [rule]: date }));
  };
  
  const handleConfirmEnforce = async (rule) => {
    if (
      (rule === 'require_stop_loss' || rule === 'max_risk_per_trade') &&
      !bracketDependentEnforceSupported
    ) {
      return;
    }
    // Save immediately when check button is clicked
    await handleSave(false);
    
    // Update enforced expiration display
    const expirationType = expirations[rule];
    const customDate = customDates[rule];
    const formatted = formatExpirationTime(expirationType, customDate);
    if (formatted) {
      setEnforcedExpirations(prev => ({ ...prev, [rule]: formatted }));
    }
  };
  
  const handleAddTradingWindow = () => {
    setTradingWindows([...tradingWindows, { startTime: '09:45', endTime: '11:00' }]);
  };
  
  const handleRemoveTradingWindow = (index) => {
    if (tradingWindows.length === 1) return;
    setTradingWindows(tradingWindows.filter((_, i) => i !== index));
  };
  
  const handleTradingWindowChange = (index, field, value) => {
    const newWindows = [...tradingWindows];
    newWindows[index][field] = value;
    setTradingWindows(newWindows);
    if (errors.trading_window) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.trading_window;
        return newErrors;
      });
    }
  };

  // Convert field name to human-readable format
  const fieldNameToHumanReadable = (fieldName) => {
    const fieldMap = {
      'max_loss_per_position': 'Max Loss Per Position',
      'max_loss_per_day': 'Max Daily Loss',
      'profit_target_per_day': 'Target Daily Profit',
      'consecutive_loss_cooldown': 'Consecutive Loss Cooldown',
      'consecutive_losses': 'Consecutive Losses',
      'consecutive_loss_cooldown_minutes': 'Cooldown Minutes',
      'max_trades_per_day': 'Max Trades Per Day',
      'max_risk_per_trade': 'Max Risk Per Trade',
      'trading_window': 'Trading Window',
      'trading_windows': 'Trading Windows'
    };
    return fieldMap[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Parse API error message to identify which field/rule failed (for highlighting only)
  // Returns the field name, preserving the original error message for display
  const parseApiErrorField = (errorMessage) => {
    if (!errorMessage) return null;
    
    const lowerMessage = errorMessage.toLowerCase();
    
    // Map error messages to field names (only for highlighting, not for message replacement)
    if (lowerMessage.includes('max_loss_per_position')) {
      return 'max_loss_per_position';
    } else if (lowerMessage.includes('max_loss_per_day')) {
      return 'max_loss_per_day';
    } else if (lowerMessage.includes('profit_target_per_day')) {
      return 'profit_target_per_day';
    } else if (lowerMessage.includes('consecutive_loss') || lowerMessage.includes('consecutive_losses')) {
      return 'consecutive_losses';
    } else if (lowerMessage.includes('max_trades_per_day')) {
      return 'max_trades_per_day';
    } else if (lowerMessage.includes('max_risk_per_trade')) {
      return 'max_risk_per_trade';
    } else if (lowerMessage.includes('trading_window') || lowerMessage.includes('trading_windows')) {
      return 'trading_window';
    }
    
    return null;
  };

  // Convert API error message to human-readable format by replacing field names
  const humanizeErrorMessage = (errorMessage, fieldName) => {
    if (!errorMessage || !fieldName) return errorMessage;
    
    const humanFieldName = fieldNameToHumanReadable(fieldName);
    // Replace the field name in the error message with human-readable version
    return errorMessage.replace(new RegExp(fieldName, 'gi'), humanFieldName);
  };

  const handleSave = async (callOnSave = true, modeOverrides = {}, options = {}) => {
    const {
      includeBehavioralTracking = false,
      applyEnforceEodForPendingQuickRules = false,
      rejectOnError = false,
      formDataOverrides = {},
    } = options;
    setSaveError(null);
    // Don't clear errors here - let them persist until user fixes them
    const effectiveModes = { ...modes, ...modeOverrides };
    // Brokers without bracket/OCO cannot persist bracket-dependent rules as Enforce
    if (!bracketDependentEnforceSupported) {
      if (effectiveModes.require_stop_loss === 'enforce') {
        effectiveModes.require_stop_loss = 'track';
      }
      if (effectiveModes.max_risk_per_trade === 'enforce') {
        effectiveModes.max_risk_per_trade = 'track';
      }
    }
    const effectiveFormData = { ...formData, ...formDataOverrides };
    
    isSavingRef.current = true;
    setSaving(true);
    setSaveStatus('saving');
    
    try {
      const existingThresholds = settings?.rule_thresholds || {};
      const existingEnforcement = settings?.enforcement_config || {};

      /** Quick-setup Done: enforce selected but not yet committed → save with end-of-session (eod) expiration */
      let expirationsForSave = { ...expirations };
      if (applyEnforceEodForPendingQuickRules && quickSetupOnly) {
        const quickEnforceRules = ['max_loss_per_day', 'trading_window', 'max_risk_per_trade'];
        for (const rule of quickEnforceRules) {
          if (effectiveModes[rule] === 'enforce') {
            const fromSettings = settings?.enforcement_config?.[rule];
            const alreadyCommitted = fromSettings?.enforce && fromSettings?.expiration;
            if (!alreadyCommitted) {
              expirationsForSave[rule] = 'eod';
            }
          }
        }
      }
      
      // Helper to get threshold value: prefer formData, fallback to existingThresholds
      // Returns the value to use, or undefined if rule is off (to allow removal)
      // IMPORTANT: If rule is enabled, formData MUST have a value (validation should have caught empty values)
      const getThresholdValue = (field, mode) => {
        if (mode === 'off') {
          // Rule is off - return undefined to allow removal
          return undefined;
        }
        // Rule is enabled - must have a threshold value from formData
        // Validation should have already ensured formData[field] is not empty
        if (effectiveFormData[field] && effectiveFormData[field].trim() !== '') {
          if (field === 'max_loss_per_day' || field === 'max_loss_per_position') {
            return -Math.abs(parseFloat(effectiveFormData[field]));
          } else if (field === 'profit_target_per_day') {
            return Math.abs(parseFloat(effectiveFormData[field]));
          } else if (field === 'consecutive_losses' || field === 'consecutive_loss_cooldown_minutes' || field === 'max_trades_per_day') {
            return parseInt(effectiveFormData[field], 10);
          } else if (field === 'max_risk_per_trade') {
            return parseFloat(effectiveFormData[field]);
          }
        }
        // If formData is empty but rule is enabled, fallback to existing threshold
        // This handles the case where user is editing one field and we want to preserve others
        // BUT: validation should have caught this, so this is a safety fallback
        const existingValue = existingThresholds[field];
        if (existingValue !== undefined && existingValue !== null) {
          return existingValue;
        }
        // Defaults for newly added rules that may be missing from older configs
        if (field === 'profit_target_per_day') return 100;
        if (field === 'consecutive_losses') return 2;
        if (field === 'consecutive_loss_cooldown_minutes') return 30;
        // If no existing value and formData is empty, return undefined
        // This will cause the threshold to be missing, which validation should have prevented
        return undefined;
      };
      
      const modeToFlags = (rule, mode) => {
        const effectiveMode = trackTier && mode === 'enforce' ? 'alert' : mode;

        if (effectiveMode === 'enforce') {
          // Determine lockout_mode based on expiration setting
          let lockout_mode = 'full';
          if (rule === 'max_trades_per_day') {
            // For max trades, use 'close_only' for end of trading session (4pm), else 'full'
            lockout_mode = expirationsForSave[rule] === 'eod' ? 'close_only' : 'full';
          }
          
          // Map expiration to lockout duration
          const expirationType = expirationsForSave[rule];
          const expirationDate = getAbsoluteExpirationDateIso(expirationType, customDates[rule]);
          const expirationConfig = {
            type: expirationType,
            ...(expirationDate ? { date: expirationDate } : {})
          };
          
          return { 
            enabled: true, 
            enforce: true,
            lockout_mode: lockout_mode,
            expiration: expirationConfig
          };
        }
        if (effectiveMode === 'alert') {
          return { enabled: true, enforce: false, alert: true };
        }
        if (effectiveMode === 'track') return { enabled: true, enforce: false, alert: false };
        return { enabled: false, enforce: false, alert: false };
      };
      
      // Build rule_thresholds object, preserving existing values for enabled rules when formData is empty
      const ruleThresholds = { ...existingThresholds };
      
      // Update thresholds - let API handle validation
      if (effectiveModes.max_trades_per_day !== 'off') {
        const maxTradesValue = getThresholdValue('max_trades_per_day', effectiveModes.max_trades_per_day);
        if (maxTradesValue !== undefined) {
          ruleThresholds.max_trades_per_day = maxTradesValue;
        }
      } else {
        delete ruleThresholds.max_trades_per_day;
      }
      
      if (effectiveModes.max_loss_per_day !== 'off') {
        const maxLossDayValue = getThresholdValue('max_loss_per_day', effectiveModes.max_loss_per_day);
        if (maxLossDayValue !== undefined) {
          ruleThresholds.max_loss_per_day = maxLossDayValue;
        }
      } else {
        delete ruleThresholds.max_loss_per_day;
      }

      if (effectiveModes.profit_target_per_day !== 'off') {
        const profitTargetValue = getThresholdValue('profit_target_per_day', effectiveModes.profit_target_per_day);
        if (profitTargetValue !== undefined) {
          ruleThresholds.profit_target_per_day = profitTargetValue;
        }
      } else {
        delete ruleThresholds.profit_target_per_day;
      }

      if (effectiveModes.consecutive_loss_cooldown !== 'off') {
        const streakValue = getThresholdValue('consecutive_losses', effectiveModes.consecutive_loss_cooldown);
        const cooldownValue = getThresholdValue('consecutive_loss_cooldown_minutes', effectiveModes.consecutive_loss_cooldown);
        if (streakValue !== undefined) ruleThresholds.consecutive_losses = streakValue;
        if (cooldownValue !== undefined) ruleThresholds.consecutive_loss_cooldown_minutes = cooldownValue;
      } else {
        delete ruleThresholds.consecutive_losses;
        delete ruleThresholds.consecutive_loss_cooldown_minutes;
      }
      
      if (effectiveModes.max_loss_per_position !== 'off') {
        const maxLossPositionValue = getThresholdValue('max_loss_per_position', effectiveModes.max_loss_per_position);
        if (maxLossPositionValue !== undefined) {
          ruleThresholds.max_loss_per_position = maxLossPositionValue;
        }
      } else {
        delete ruleThresholds.max_loss_per_position;
      }
      
      if (effectiveModes.max_risk_per_trade !== 'off') {
        const maxRiskValue = getThresholdValue('max_risk_per_trade', effectiveModes.max_risk_per_trade);
        if (maxRiskValue !== undefined) {
          ruleThresholds.max_risk_per_trade = maxRiskValue;
        }
      } else {
        delete ruleThresholds.max_risk_per_trade;
      }
      
      if (effectiveModes.trading_window !== 'off') {
        ruleThresholds.trading_windows = tradingWindows.map(w => ({
          startTime: `${w.startTime}:00`,
          endTime: `${w.endTime}:00`
        }));
        ruleThresholds.trading_window_timezone = ET_TIMEZONE;
      } else {
        delete ruleThresholds.trading_windows;
        delete ruleThresholds.trading_window_timezone;
      }
      
      if (effectiveModes.require_stop_loss !== 'off') {
        ruleThresholds.require_stop_loss = true;
      }
      
      const config = {
        rule_thresholds: ruleThresholds,
        enforcement_config: {
          max_trades_per_day: modeToFlags('max_trades_per_day', effectiveModes.max_trades_per_day),
          max_loss_per_day: modeToFlags('max_loss_per_day', effectiveModes.max_loss_per_day),
          profit_target_per_day: modeToFlags('profit_target_per_day', effectiveModes.profit_target_per_day),
          consecutive_loss_cooldown: modeToFlags('consecutive_loss_cooldown', effectiveModes.consecutive_loss_cooldown),
          max_loss_per_position: modeToFlags('max_loss_per_position', effectiveModes.max_loss_per_position),
          max_risk_per_trade: modeToFlags('max_risk_per_trade', effectiveModes.max_risk_per_trade),
          trading_window: modeToFlags('trading_window', effectiveModes.trading_window),
          require_stop_loss: modeToFlags('require_stop_loss', effectiveModes.require_stop_loss),
          manual_lockout: isManualLockoutActive()
            ? { enabled: true, enforce: true }
            : { enabled: false, enforce: false },
          // Only send behavioral toggles when explicitly saving them; otherwise keep server values so toggling doesn't trigger config update
          stop_take_profit_modifications: includeBehavioralTracking
            ? { enabled: effectiveModes.stop_take_profit_modifications === 'track', enforce: false }
            : (existingEnforcement.stop_take_profit_modifications ?? { enabled: false, enforce: false }),
          journal_tracking: includeBehavioralTracking
            ? { enabled: effectiveModes.journal_tracking === 'track', enforce: false }
            : (existingEnforcement.journal_tracking ?? { enabled: false, enforce: false })
        }
      };
      
      await saveSettings(config);
      
      // saveSettings already updates the hook state via setSettings(response)
      // No need to refetch - the state update will propagate to all components using the same hook instance
      // This prevents duplicate requests (POST + GET) when saveSettings already updated the state
      
      // Update enforced expirations state - only show pill if enforcement is actually active
      const newEnforcedExpirations = {};
      ['max_loss_per_day', 'profit_target_per_day', 'consecutive_loss_cooldown', 'max_trades_per_day', 'trading_window', 'require_stop_loss', 'max_risk_per_trade'].forEach(rule => {
        // Check if enforcement is active in the saved config (not just local mode state)
        const savedEnforcement = config.enforcement_config[rule];
        if (savedEnforcement?.enforce && savedEnforcement?.expiration) {
          const exp = savedEnforcement.expiration;
          const formatted = formatExpirationTime(exp.type, exp.date);
          if (formatted) {
            newEnforcedExpirations[rule] = formatted;
          }
        }
      });
      setEnforcedExpirations(newEnforcedExpirations);

      if (applyEnforceEodForPendingQuickRules && quickSetupOnly) {
        setExpirations(expirationsForSave);
      }
      
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
      
      // Update last saved settings ref with what we just saved
      // This prevents useEffect from reloading state immediately after save
      lastSavedSettingsRef.current = JSON.parse(JSON.stringify(config));
      
      if (callOnSave && onSave) {
        onSave();
      }
      
      setSaving(false);
      // Delay to allow parent to update settings prop, then allow useEffect to run again
      setTimeout(() => {
        isSavingRef.current = false;
        // Update ref with current settings after parent has updated
        if (settings) {
          lastSavedSettingsRef.current = JSON.parse(JSON.stringify(settings));
        }
      }, 300);
    } catch (error) {
      console.error('Error saving trading rules:', error);
      // Extract error message - check multiple possible locations
      const errorMessage = error.error || error.message || 'Failed to save settings';
      
      setSaveStatus('idle');
      setSaving(false);
      isSavingRef.current = false;
      
      // Parse API error to identify which field failed (for highlighting only)
      // Use the original error message for display to preserve granularity
      const errorField = parseApiErrorField(errorMessage);
      
      if (errorField) {
        // Convert error message to human-readable format
        const humanErrorMessage = humanizeErrorMessage(errorMessage, errorField);
        
        // Set error state with humanized API message (displayed below input)
        // Don't set saveError to avoid showing duplicate error messages
        setErrors({ [errorField]: humanErrorMessage });
        
        // Scroll to the error field
        setTimeout(() => {
          const inputElement = document.querySelector(`input[data-field="${errorField}"]`);
          
          if (inputElement) {
            const card = inputElement.closest('[style*="background"], [style*="border"]');
            if (card) {
              card.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            setTimeout(() => {
              inputElement.focus();
            }, 300);
          }
        }, 100);
      } else {
        // If we can't parse the field, show general error message
        setSaveError(errorMessage);
      }
      if (rejectOnError) {
        throw error;
      }
    }
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
    ...(quickSetupOnly
      ? {
          quickSetupFlushPendingEnforce: () =>
            handleSave(false, {}, { applyEnforceEodForPendingQuickRules: true, rejectOnError: true }),
        }
      : {}),
  }));

  const getEnforcementLockExpirationTime = (rule) => {
    const enforcementConfig = settings?.enforcement_config?.[rule];
    if (!enforcementConfig?.enforce || !enforcementConfig?.expiration) return null;
    const exp = enforcementConfig.expiration;
    const absoluteIso = exp.date || getAbsoluteExpirationDateIso(exp.type, exp.date);
    if (!absoluteIso) return null;
    if (moment(absoluteIso).isSameOrBefore(moment())) return null;
    return moment(absoluteIso).tz(ET_TIMEZONE).format('ddd, MMM D, h:mm A');
  };

  // Helper to check if a rule is currently locked/enforced
  const isRuleLocked = (rule) => {
    const enforcementConfig = settings?.enforcement_config?.[rule];
    if (enforcementConfig?.enforce !== true) return false;
    // If an expiration was recorded, the lock is only active while that expiration
    // is still in the future — once it lapses the rule must be editable again,
    // even if the server hasn't flipped `enforce` back to false yet.
    if (enforcementConfig?.expiration) {
      return !!getEnforcementLockExpirationTime(rule);
    }
    // No expiration recorded (legacy configs) — fall back to indefinite lock while enforced.
    return true;
  };

  // Manual lockout: active only when server has active lockout with future expires_at
  const getManualLockoutExpirationTime = () => {
    const ml = settings?.rule_thresholds?.manual_lockout;
    if (!ml?.active || !ml?.expires_at) return null;
    const expiresAt = new Date(ml.expires_at);
    if (expiresAt <= new Date()) return null;
    return expiresAt.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isManualLockoutActive = () => !!getManualLockoutExpirationTime();

  // Active consecutive-loss cooldown lockout (short-lived pause), distinct from
  // enforcement_config.expiration which locks the Enforce setting itself.
  const getActiveCooldownExpiresTime = () => {
    // Re-evaluate when the minute tick advances so expired cooldowns clear from the UI.
    void currentMinutes;
    if (!accountId || !Array.isArray(pendingAlerts) || pendingAlerts.length === 0) return null;
    const now = new Date();
    // dismissed_at is intentionally ignored: the cooldown keeps blocking trades after the
    // popup is dismissed, so this label should too (matches Trading Lockouts panel).
    const alert = pendingAlerts.find((a) =>
      a.account_id === accountId &&
      a.rule_type === 'consecutive_loss_cooldown' &&
      !a.archived_at &&
      a.expires_at &&
      new Date(a.expires_at) > now
    );
    if (!alert) return null;
    return formatLockoutExpiresAt(alert.expires_at, 'long');
  };

  // Compute whether current local time falls outside all configured trading windows
  const isOutsideHours = modes.trading_window !== 'off' && tradingWindows.length > 0 && (() => {
    const timezone = ET_TIMEZONE;
    try {
      const now = new Date();
      const timeString = now.toLocaleString('en-US', {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      });
      const [h, m] = timeString.split(':').map(Number);
      const tzMinutes = h * 60 + m;
      return !tradingWindows.some(w => {
        const [sh, sm] = w.startTime.split(':').map(Number);
        const [eh, em] = w.endTime.split(':').map(Number);
        return tzMinutes >= sh * 60 + sm && tzMinutes < eh * 60 + em;
      });
    } catch {
      return false;
    }
  })();
  // Depend on currentMinutes so it re-evaluates every minute
  void currentMinutes;

  const handleManualLockoutModeChange = (enforce) => {
    if (enforce) {
      setModes(prev => ({ ...prev, manual_lockout: 'enforce' }));
      if (!customDates.manual_lockout) {
        const today = new Date();
        today.setHours(16, 0, 0, 0);
        setCustomDates(prev => ({ ...prev, manual_lockout: today.toISOString().slice(0, 16) }));
      }
    } else {
      if (isManualLockoutActive()) {
        return; // Block disabling while lockout is active (until it expires)
      }
      setModes(prev => ({ ...prev, manual_lockout: 'off' }));
    }
  };

  const getManualLockoutExpiresAtISO = () => {
    const type = expirations.manual_lockout || 'eod';
    const tz = 'America/New_York';
    if (type === 'custom' && customDates.manual_lockout) {
      return new Date(customDates.manual_lockout).toISOString();
    }
    if (type === '1_week') {
      return moment().tz(tz).add(1, 'week').toISOString();
    }
    if (type === '1_month') {
      return moment().tz(tz).add(1, 'month').toISOString();
    }
    // 'eod' / 'end_of_day' (default): end of day, 11:59 p.m. ET — not 4 p.m. market close.
    let dayEnd = moment().tz(tz).hour(23).minute(59).second(0).millisecond(0);
    if (dayEnd.isBefore(moment())) dayEnd = dayEnd.add(1, 'day');
    return dayEnd.toISOString();
  };

  const handleConfirmManualLockout = async () => {
    const expiresAt = getManualLockoutExpiresAtISO();
    if (new Date(expiresAt) <= new Date()) {
      setSaveError('Expiration must be in the future');
      return;
    }
    if (!accountId) {
      setSaveError('Cannot enable lockout: no account selected');
      return;
    }
    setManualLockoutSaving(true);
    setSaveError(null);
    try {
      const json = await request(
        `${BACKEND_URL}/enforcement/manual_lockout`,
        {},
        'POST',
        { account_id: accountId, expires_at: expiresAt }
      );
      if (json?.error) {
        setSaveError(json.error);
      } else {
        if (json?.config) applySettings(json.config);
        fetchPendingAlerts();
      }
    } catch (err) {
      setSaveError(err?.message || err?.error || 'Failed to enable manual lockout');
    } finally {
      setManualLockoutSaving(false);
    }
  };

  const handleManualLockoutExpirationChange = (val) => {
    setExpirations(prev => ({ ...prev, manual_lockout: val }));
    setManualLockoutExpirationOpen(false);
    if (val === 'custom' && !customDates.manual_lockout) {
      const today = new Date();
      today.setHours(16, 0, 0, 0);
      setCustomDates(prev => ({ ...prev, manual_lockout: today.toISOString().slice(0, 16) }));
    }
  };

  const handleDisableManualLockout = async () => {
    if (!accountId) return;
    setManualLockoutSaving(true);
    setSaveError(null);
    try {
      const json = await request(
        `${BACKEND_URL}/enforcement/manual_lockout?account_id=${accountId}`,
        {},
        'DELETE'
      );
      if (json?.error) {
        setSaveError(json.error);
      } else {
        setModes(prev => ({ ...prev, manual_lockout: 'off' }));
        if (json?.config) applySettings(json.config);
        fetchPendingAlerts();
      }
    } catch (err) {
      setSaveError(err?.message || err?.error || 'Failed to disable manual lockout');
    } finally {
      setManualLockoutSaving(false);
    }
  };

  const manualLockoutCard = (
    <ManualLockoutCard
      mode={modes.manual_lockout}
      onModeChange={handleManualLockoutModeChange}
      saving={manualLockoutSaving}
      isActive={isManualLockoutActive()}
      lockoutExpirationTime={getManualLockoutExpirationTime()}
      expiration={expirations.manual_lockout}
      onExpirationChange={handleManualLockoutExpirationChange}
      customDate={customDates.manual_lockout}
      onCustomDateChange={(val) => setCustomDates(prev => ({ ...prev, manual_lockout: val }))}
      onConfirmEnforce={handleConfirmManualLockout}
      expirationDropdownOpen={manualLockoutExpirationOpen}
      onExpirationDropdownToggle={setManualLockoutExpirationOpen}
    />
  );

  const rulesAndAlertsBlock = (
    <>
      <SectionHeader
        title="Trading Rules"
        subtitle={
          trackTier
            ? 'Track alerts you the moment you break a rule. Upgrade to Trade to block the order in-app.'
            : quickSetupOnly
              ? (
                <>
                  <p style={{ margin: 0 }}>
                    When a limit is hit, new trades are blocked. Existing positions can still be closed and managed.
                  </p>
                  <div>
                    <p style={{
                      margin: '0 0 4px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      textTransform: 'uppercase',
                      opacity: 0.9,
                    }}>
                      Track vs Enforce
                    </p>
                    <p style={{ margin: 0 }}>
                      <strong>Track</strong> alerts you but still allows new positions.
                      {' '}
                      <strong>Enforce</strong> locks you out of new trades until the end of the day.
                    </p>
                  </div>
                </>
              )
              : 'These rules can prevent order execution when limits are exceeded. Existing positions can still be closed and managed.'
        }
        variant={quickSetupOnly ? 'tradingRulesQuick' : 'default'}
      />

      {/* Max Daily Loss */}
      <RuleCard
        title="Max Daily Loss"
        description="Stops trading when you reach your daily loss limit"
        mode={modes.max_loss_per_day}
        onModeChange={(mode) => handleModeChangeWithSave('max_loss_per_day', mode)}
        showExpiration={!trackTier}
        expirationValue={expirations.max_loss_per_day}
        onExpirationChange={(exp) => handleExpirationChange('max_loss_per_day', exp)}
        customDateValue={customDates.max_loss_per_day}
        onCustomDateChange={(date) => handleCustomDateChange('max_loss_per_day', date)}
        onConfirmEnforce={() => handleConfirmEnforce('max_loss_per_day')}
        ruleKey="max_loss_per_day"
        expirationDropdownKey="max_loss_per_day"
        expirationDropdownOpen={openExpirationKey === 'max_loss_per_day'}
        onExpirationDropdownToggle={setOpenExpirationKey}
        restrictsTrading={!trackTier}
        alertOnly={trackTier}
        enforcedExpirationTime={trackTier ? null : getEnforcementLockExpirationTime('max_loss_per_day')}
        limitExceeded={!!(formData.max_loss_per_day && parseFloat(formData.max_loss_per_day) > 0 && typeof todayChange === 'number' && todayChange < 0 && Math.abs(todayChange) >= parseFloat(formData.max_loss_per_day))}
      >
        {modes.max_loss_per_day !== 'off' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <span style={{ 
                position: 'absolute', 
                left: '10px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: colors.textMuted,
                fontSize: '0.875rem',
                pointerEvents: 'none',
                zIndex: 1
              }}>
                $
              </span>
              <Form.Control
                type="text"
                value={formData.max_loss_per_day}
                onChange={(e) => handleInputChange('max_loss_per_day', e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))}
                onFocus={() => { focusedFieldRef.current = 'max_loss_per_day'; }}
                onBlur={(e) => handleInputBlur('max_loss_per_day', e.target.value)}
                placeholder="0.00"
                disabled={isRuleLocked('max_loss_per_day')}
                data-field="max_loss_per_day"
                style={{
                  width: '140px',
                  paddingLeft: '24px',
                  backgroundColor: colors.bgCard,
                  borderColor: errors.max_loss_per_day ? colors.danger : colors.border,
                  color: colors.text,
                  fontSize: '0.875rem',
                  opacity: isRuleLocked('max_loss_per_day') ? 0.6 : 1
                }}
              />
            </div>
            {errors.max_loss_per_day && (
              <div style={{
                fontSize: '0.7rem',
                color: colors.danger,
                fontStyle: 'italic'
              }}>
                {errors.max_loss_per_day}
              </div>
            )}
            {typeof todayChange === 'number' && (() => {
              const limit = parseFloat(formData.max_loss_per_day);
              const exceeded =
                Number.isFinite(limit) &&
                limit > 0 &&
                todayChange < 0 &&
                Math.abs(todayChange) >= limit;
              const formatted = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(todayChange);
              return (
                <span
                  style={{
                    fontSize: '0.875rem',
                    color: exceeded ? colors.danger : colors.textMuted,
                    fontWeight: exceeded ? 600 : 400,
                  }}
                >
                  {formatted} today
                </span>
              );
            })()}
          </div>
        )}
      </RuleCard>

      {/* Target Daily Profit */}
      <RuleCard
        title="Target Daily Profit"
        description="Locks out new trades once you hit your daily profit target — protect the win"
        mode={modes.profit_target_per_day}
        onModeChange={(mode) => handleModeChangeWithSave('profit_target_per_day', mode)}
        showExpiration={!trackTier}
        expirationValue={expirations.profit_target_per_day}
        onExpirationChange={(exp) => handleExpirationChange('profit_target_per_day', exp)}
        customDateValue={customDates.profit_target_per_day}
        onCustomDateChange={(date) => handleCustomDateChange('profit_target_per_day', date)}
        onConfirmEnforce={() => handleConfirmEnforce('profit_target_per_day')}
        ruleKey="profit_target_per_day"
        expirationDropdownKey="profit_target_per_day"
        expirationDropdownOpen={openExpirationKey === 'profit_target_per_day'}
        onExpirationDropdownToggle={setOpenExpirationKey}
        restrictsTrading={!trackTier}
        alertOnly={trackTier}
        enforcedExpirationTime={trackTier ? null : getEnforcementLockExpirationTime('profit_target_per_day')}
        limitExceeded={!!(formData.profit_target_per_day && parseFloat(formData.profit_target_per_day) > 0 && typeof todayChange === 'number' && todayChange >= parseFloat(formData.profit_target_per_day))}
      >
        {modes.profit_target_per_day !== 'off' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <span style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.textMuted,
                fontSize: '0.875rem',
                pointerEvents: 'none',
                zIndex: 1
              }}>
                $
              </span>
              <Form.Control
                type="text"
                value={formData.profit_target_per_day}
                onChange={(e) => handleInputChange('profit_target_per_day', e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))}
                onFocus={() => { focusedFieldRef.current = 'profit_target_per_day'; }}
                onBlur={(e) => handleInputBlur('profit_target_per_day', e.target.value)}
                placeholder="0.00"
                disabled={isRuleLocked('profit_target_per_day')}
                data-field="profit_target_per_day"
                style={{
                  width: '140px',
                  paddingLeft: '24px',
                  backgroundColor: colors.bgCard,
                  borderColor: errors.profit_target_per_day ? colors.danger : colors.border,
                  color: colors.text,
                  fontSize: '0.875rem',
                  opacity: isRuleLocked('profit_target_per_day') ? 0.6 : 1
                }}
              />
            </div>
            {errors.profit_target_per_day && (
              <div style={{ fontSize: '0.7rem', color: colors.danger, fontStyle: 'italic' }}>
                {errors.profit_target_per_day}
              </div>
            )}
            {typeof todayChange === 'number' && (() => {
              const limit = parseFloat(formData.profit_target_per_day);
              const hit = Number.isFinite(limit) && limit > 0 && todayChange >= limit;
              const formatted = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(todayChange);
              return (
                <span style={{
                  fontSize: '0.875rem',
                  color: hit ? (colors.success || colors.chartUp) : colors.textMuted,
                  fontWeight: hit ? 600 : 400,
                }}>
                  {formatted} today
                </span>
              );
            })()}
          </div>
        )}
      </RuleCard>

      {/* Consecutive Loss Cooldown */}
      <RuleCard
        title="Consecutive Loss Cooldown"
        description="After N losing trades in a row today, pause new trades for a cooldown period"
        mode={modes.consecutive_loss_cooldown}
        onModeChange={(mode) => handleModeChangeWithSave('consecutive_loss_cooldown', mode)}
        showExpiration={!trackTier}
        expirationValue={expirations.consecutive_loss_cooldown}
        onExpirationChange={(exp) => handleExpirationChange('consecutive_loss_cooldown', exp)}
        customDateValue={customDates.consecutive_loss_cooldown}
        onCustomDateChange={(date) => handleCustomDateChange('consecutive_loss_cooldown', date)}
        onConfirmEnforce={() => handleConfirmEnforce('consecutive_loss_cooldown')}
        ruleKey="consecutive_loss_cooldown"
        expirationDropdownKey="consecutive_loss_cooldown"
        expirationDropdownOpen={openExpirationKey === 'consecutive_loss_cooldown'}
        onExpirationDropdownToggle={setOpenExpirationKey}
        restrictsTrading={!trackTier}
        alertOnly={trackTier}
        enforcedExpirationTime={trackTier ? null : getEnforcementLockExpirationTime('consecutive_loss_cooldown')}
        cooldownExpiresTime={getActiveCooldownExpiresTime()}
      >
        {modes.consecutive_loss_cooldown !== 'off' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <Form.Control
              type="number"
              min="1"
              value={formData.consecutive_losses}
              onChange={(e) => handleInputChange('consecutive_losses', e.target.value.replace(/[^0-9]/g, ''))}
              onFocus={() => { focusedFieldRef.current = 'consecutive_losses'; }}
              onBlur={(e) => handleInputBlur('consecutive_losses', e.target.value)}
              placeholder="losses"
              disabled={isRuleLocked('consecutive_loss_cooldown')}
              data-field="consecutive_losses"
              style={{
                width: '100px',
                backgroundColor: colors.bgCard,
                borderColor: errors.consecutive_losses ? colors.danger : colors.border,
                color: colors.text,
                fontSize: '0.875rem',
                opacity: isRuleLocked('consecutive_loss_cooldown') ? 0.6 : 1
              }}
            />
            <span style={{ fontSize: '0.875rem', color: colors.textMuted }}>losses →</span>
            <Form.Control
              type="number"
              min="1"
              value={formData.consecutive_loss_cooldown_minutes}
              onChange={(e) => handleInputChange('consecutive_loss_cooldown_minutes', e.target.value.replace(/[^0-9]/g, ''))}
              onFocus={() => { focusedFieldRef.current = 'consecutive_loss_cooldown_minutes'; }}
              onBlur={(e) => handleInputBlur('consecutive_loss_cooldown_minutes', e.target.value)}
              placeholder="mins"
              disabled={isRuleLocked('consecutive_loss_cooldown')}
              data-field="consecutive_loss_cooldown_minutes"
              style={{
                width: '100px',
                backgroundColor: colors.bgCard,
                borderColor: errors.consecutive_loss_cooldown_minutes ? colors.danger : colors.border,
                color: colors.text,
                fontSize: '0.875rem',
                opacity: isRuleLocked('consecutive_loss_cooldown') ? 0.6 : 1
              }}
            />
            <span style={{ fontSize: '0.875rem', color: colors.textMuted }}>min cooldown</span>
            {(errors.consecutive_losses || errors.consecutive_loss_cooldown_minutes) && (
              <div style={{ fontSize: '0.7rem', color: colors.danger, fontStyle: 'italic', width: '100%' }}>
                {errors.consecutive_losses || errors.consecutive_loss_cooldown_minutes}
              </div>
            )}
          </div>
        )}
      </RuleCard>

      {/* Max Trades Per Day */}
      <RuleCard
        title="Max Trades Per Day"
        description={'Limits the number of trades you can execute in one day.\n\nWhat counts as a trade: opening a new position, or increasing size on a position.\n\nWhat does not count: closing a position or reducing size, including partial exits.'}
        mode={modes.max_trades_per_day}
        onModeChange={(mode) => handleModeChangeWithSave('max_trades_per_day', mode)}
        showExpiration={!trackTier}
        expirationValue={expirations.max_trades_per_day}
        onExpirationChange={(exp) => handleExpirationChange('max_trades_per_day', exp)}
        customDateValue={customDates.max_trades_per_day}
        onCustomDateChange={(date) => handleCustomDateChange('max_trades_per_day', date)}
        onConfirmEnforce={() => handleConfirmEnforce('max_trades_per_day')}
        ruleKey="max_trades_per_day"
        expirationDropdownKey="max_trades_per_day"
        expirationDropdownOpen={openExpirationKey === 'max_trades_per_day'}
        onExpirationDropdownToggle={setOpenExpirationKey}
        restrictsTrading={!trackTier}
        alertOnly={trackTier}
        enforcedExpirationTime={trackTier ? null : getEnforcementLockExpirationTime('max_trades_per_day')}
        limitExceeded={!!(
          formData.max_trades_per_day &&
          parseInt(formData.max_trades_per_day, 10) > 0 &&
          typeof todayTradesTaken === 'number' &&
          todayTradesTaken >= parseInt(formData.max_trades_per_day, 10)
        )}
      >
        {modes.max_trades_per_day !== 'off' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <Form.Control
                type="number"
                min="1"
                value={formData.max_trades_per_day}
                onChange={(e) => handleInputChange('max_trades_per_day', e.target.value)}
                onFocus={() => { focusedFieldRef.current = 'max_trades_per_day'; }}
                onBlur={(e) => handleInputBlur('max_trades_per_day', e.target.value)}
                placeholder="# of trades"
                disabled={isRuleLocked('max_trades_per_day')}
                data-field="max_trades_per_day"
                style={{
                  width: '140px',
                  backgroundColor: colors.bgCard,
                  borderColor: errors.max_trades_per_day ? colors.danger : colors.border,
                  color: colors.text,
                  fontSize: '0.875rem',
                  opacity: isRuleLocked('max_trades_per_day') ? 0.6 : 1
                }}
              />
              {errors.max_trades_per_day && (
                <div style={{
                  fontSize: '0.7rem',
                  color: colors.danger,
                  marginTop: '4px',
                  fontStyle: 'italic'
                }}>
                  {errors.max_trades_per_day}
                </div>
              )}
              {typeof todayTradesTaken === 'number' && (
                <span style={{ fontSize: '0.875rem', color: colors.textMuted }}>
                  {todayTradesTaken} trade{todayTradesTaken !== 1 ? 's' : ''} today
                </span>
              )}
            </div>
        )}
      </RuleCard>

      {/* Trading Time Windows */}
      <RuleCard
        title="Allowed Trading Hours"
        titleSuffix="(ET)"
        titleSuffixTooltip="Eastern Time (America/New_York, EST/EDT)"
        description="Only trade during specified time windows. Timezone: Eastern Time (America/New_York, EST/EDT)."
        mode={modes.trading_window}
        onModeChange={(mode) => handleModeChangeWithSave('trading_window', mode)}
        showExpiration={!trackTier}
        expirationValue={expirations.trading_window}
        onExpirationChange={(val) => handleExpirationChange('trading_window', val)}
        customDateValue={customDates.trading_window}
        onCustomDateChange={(date) => handleCustomDateChange('trading_window', date)}
        onConfirmEnforce={() => handleConfirmEnforce('trading_window')}
        expirationDropdownKey="trading_window"
        expirationDropdownOpen={openExpirationKey === 'trading_window'}
        onExpirationDropdownToggle={setOpenExpirationKey}
        ruleKey="trading_window"
        restrictsTrading={!trackTier}
        alertOnly={trackTier}
        restrictionText="Restricts trading when outside allowed trading hours"
        enforcedExpirationTime={trackTier ? null : getEnforcementLockExpirationTime('trading_window')}
        outsideHours={isOutsideHours}
      >
        {modes.trading_window !== 'off' && (
          <TradingWindowsList
            windows={tradingWindows}
            onWindowChange={handleTradingWindowChange}
            onAdd={handleAddTradingWindow}
            onRemove={handleRemoveTradingWindow}
            isLocked={isRuleLocked('trading_window')}
            addButtonOnNextRow={quickSetupOnly}
          />
        )}
      </RuleCard>

      {/* Require Stop Loss */}
      <RuleCard
        title="Require Stop Loss"
        description={
          stopLossEnforceSupported
            ? 'Requires a stop loss when opening a new position. Adding to a position you already hold is not blocked.'
            : 'Tracks when you open a position without a stop loss. Your broker does not support submitting trades with a stop-loss, so this rule cannot be enforced.'
        }
        mode={modes.require_stop_loss}
        onModeChange={(mode) => handleModeChangeWithSave('require_stop_loss', mode)}
        showExpiration={!trackTier && stopLossEnforceSupported}
        expirationValue={expirations.require_stop_loss}
        onExpirationChange={(val) => handleExpirationChange('require_stop_loss', val)}
        customDateValue={customDates.require_stop_loss}
        onCustomDateChange={(date) => handleCustomDateChange('require_stop_loss', date)}
        onConfirmEnforce={() => handleConfirmEnforce('require_stop_loss')}
        expirationDropdownKey="require_stop_loss"
        expirationDropdownOpen={openExpirationKey === 'require_stop_loss'}
        onExpirationDropdownToggle={setOpenExpirationKey}
        ruleKey="require_stop_loss"
        restrictsTrading={!trackTier && stopLossEnforceSupported}
        alertOnly={trackTier}
        enforcedExpirationTime={
          trackTier || !stopLossEnforceSupported
            ? null
            : getEnforcementLockExpirationTime('require_stop_loss')
        }
        restrictionText="Blocks trades without stop loss"
        disabledModes={stopLossEnforceSupported || trackTier ? [] : ['enforce']}
        disabledModeTooltips={
          stopLossEnforceSupported || trackTier
            ? {}
            : { enforce: BRACKET_DEPENDENT_RULE_ENFORCE_UNSUPPORTED_TOOLTIP }
        }
      >
        {/* No input needed */}
      </RuleCard>

      {/* Max Risk Per Trade */}
      <RuleCard
        title="Max Risk Per Trade"
        description={
          maxRiskEnforceSupported
            ? 'Limits the maximum risk amount per trade. Applies to trades with stop loss or bracket orders with stop loss.'
            : 'Tracks risk on trades that include a stop loss. Your broker does not support submitting trades with a stop-loss, so this rule cannot be enforced.'
        }
        mode={modes.max_risk_per_trade}
        onModeChange={(mode) => handleModeChangeWithSave('max_risk_per_trade', mode)}
        showExpiration={!trackTier && maxRiskEnforceSupported}
        expirationValue={expirations.max_risk_per_trade}
        onExpirationChange={(exp) => handleExpirationChange('max_risk_per_trade', exp)}
        customDateValue={customDates.max_risk_per_trade}
        onCustomDateChange={(date) => handleCustomDateChange('max_risk_per_trade', date)}
        onConfirmEnforce={() => handleConfirmEnforce('max_risk_per_trade')}
        expirationDropdownKey="max_risk_per_trade"
        expirationDropdownOpen={openExpirationKey === 'max_risk_per_trade'}
        onExpirationDropdownToggle={setOpenExpirationKey}
        ruleKey="max_risk_per_trade"
        restrictsTrading={!trackTier && maxRiskEnforceSupported}
        alertOnly={trackTier}
        enforcedExpirationTime={
          trackTier || !maxRiskEnforceSupported
            ? null
            : getEnforcementLockExpirationTime('max_risk_per_trade')
        }
        restrictionText="Blocks trades that exceed max risk per trade"
        disabledModes={maxRiskEnforceSupported || trackTier ? [] : ['enforce']}
        disabledModeTooltips={
          maxRiskEnforceSupported || trackTier
            ? {}
            : { enforce: BRACKET_DEPENDENT_RULE_ENFORCE_UNSUPPORTED_TOOLTIP }
        }
      >
        {modes.max_risk_per_trade !== 'off' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <span style={{ 
                position: 'absolute', 
                left: '10px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: colors.textMuted,
                fontSize: '0.875rem',
                pointerEvents: 'none',
                zIndex: 1
              }}>
                $
              </span>
              <Form.Control
                type="text"
                value={formData.max_risk_per_trade}
                onChange={(e) => handleInputChange('max_risk_per_trade', e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))}
                onFocus={() => { focusedFieldRef.current = 'max_risk_per_trade'; }}
                onBlur={(e) => handleInputBlur('max_risk_per_trade', e.target.value)}
                placeholder="0.00"
                disabled={isRuleLocked('max_risk_per_trade')}
                data-field="max_risk_per_trade"
                style={{
                  width: '140px',
                  paddingLeft: '24px',
                  backgroundColor: colors.bgCard,
                  borderColor: errors.max_risk_per_trade ? colors.danger : colors.border,
                  color: colors.text,
                  fontSize: '0.875rem',
                  opacity: isRuleLocked('max_risk_per_trade') ? 0.6 : 1
                }}
              />
            </div>
            {errors.max_risk_per_trade && (
              <div style={{
                fontSize: '0.7rem',
                color: colors.danger,
                fontStyle: 'italic'
              }}>
                {errors.max_risk_per_trade}
              </div>
            )}
          </div>
        )}
      </RuleCard>

      <SectionHeader 
        title="Trade Alerts"
        subtitle="Receive notifications when thresholds are reached. These never block trades."
      />

      {/* Max Loss Per Position */}
      <RuleCard
        title="Max Loss Per Position"
        description="Sends alert when any position for the account exceeds the max position loss"
        mode={modes.max_loss_per_position === 'enforce' ? 'track' : modes.max_loss_per_position}
        onModeChange={(mode) => handleModeChangeWithSave('max_loss_per_position', mode === 'enforce' ? 'track' : mode)}
        showExpiration={false}
        alertOnly={true}
      >
        {modes.max_loss_per_position !== 'off' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <span style={{ 
                position: 'absolute', 
                left: '10px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: colors.textMuted,
                fontSize: '0.875rem',
                pointerEvents: 'none',
                zIndex: 1
              }}>
                $
              </span>
              <Form.Control
                type="text"
                value={formData.max_loss_per_position}
                onChange={(e) => handleInputChange('max_loss_per_position', e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))}
                onFocus={() => { focusedFieldRef.current = 'max_loss_per_position'; }}
                onBlur={(e) => handleInputBlur('max_loss_per_position', e.target.value)}
                placeholder="0.00"
                data-field="max_loss_per_position"
                style={{
                  width: '140px',
                  paddingLeft: '24px',
                  backgroundColor: colors.bgCard,
                  borderColor: errors.max_loss_per_position ? colors.danger : colors.border,
                  color: colors.text,
                  fontSize: '0.875rem'
                }}
              />
            </div>
            {errors.max_loss_per_position && (
              <div style={{
                fontSize: '0.7rem',
                color: colors.danger,
                fontStyle: 'italic'
              }}>
                {errors.max_loss_per_position}
              </div>
            )}
          </div>
        )}
      </RuleCard>

      <SectionHeader 
        title="Behavioral Tracking"
        subtitle="Set which behaviors to track when detecting rule violations. These never block trades."
      />

      <BehavioralTrackingSection
        trackStopMods={modes.stop_take_profit_modifications === 'track'}
        trackJournal={modes.journal_tracking === 'track'}
        onTrackStopModsChange={(checked) => handleModeChange('stop_take_profit_modifications', checked ? 'track' : 'off')}
        onTrackJournalChange={(checked) => handleModeChange('journal_tracking', checked ? 'track' : 'off')}
      />
    </>
  );

  const cardWrapStyle = {
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    // Tighter padding on mobile — Portfolio already applies page padding, and rule cards
    // need horizontal room for the Off/Track/Enforce control.
    padding: isMobile ? '12px' : '1.25rem',
    backgroundColor: colors.bgCard
  };

  if (quickSetupOnly) {
    return (
      <div style={{ color: colors.text }}>
        {saveError && (
          <Alert variant="danger" dismissible onClose={() => setSaveError(null)} style={{ marginBottom: '16px' }}>
            {saveError}
          </Alert>
        )}
        {rulesAndAlertsBlock}
      </div>
    );
  }

  return (
    <div style={{ color: colors.text }}>
      {saveError && (
        <Alert variant="danger" dismissible onClose={() => setSaveError(null)} style={{ marginBottom: '16px' }}>
          {saveError}
        </Alert>
      )}

      {layoutVariant === 'ruleConfigurationPage' ? (
        <>
          {!trackTier && <div style={{ marginBottom: '20px' }}>{manualLockoutCard}</div>}
          <div style={cardWrapStyle}>{rulesAndAlertsBlock}</div>
        </>
      ) : (
        <>
          {!trackTier && manualLockoutCard}
          {rulesAndAlertsBlock}
        </>
      )}
    </div>
  );
});

export default TradingRulesForm;
